import uniqueId from '../../utils/uniqueId';
import { API } from 'aws-amplify';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';

import { ReactComponent as UploadIcon } from '../../image/file-upload-icon.svg';
import { ReactComponent as DownloadIcon } from '../../image/file-download-icon.svg';
import { ReactComponent as DeleteIcon } from '../../image/trashcan-icon.svg';
import DateTimeText from './DateTimeText';
import { UserStateContext, FormContext } from '../Context';

import './FileDrop.scss';

const FileDrop = forwardRef((props, ref) => {
    const { doingAjax } = useContext(UserStateContext);
    const { triggerValidation, validationFinished } = useContext(FormContext);

    const [uid] = useState(uniqueId());
    const [classes] = useState(props?.classes ? props.classes.join(' ') + ' file-drop' : 'file-drop');
    const [files, setFiles] = useState(props?.files ? props.files : null);
    const [labelText] = useState(!!props?.labelText ? props.labelText : null);
    const [retrieving, setRetrieving] = useState(false);
    const [valid, setValid] = useState(true);
    const [required, setRequired] = useState(typeof props?.required === 'boolean' ? props.required : false);
    const [downloadURL, setDownloadURL] = useState();
    const [uploading, setUploading] = useState(false);
    const [disabled, setDisabled] = useState(typeof props?.disabled === 'boolean' ? props.disabled : doingAjax ? doingAjax : false);
    const [downloadOnly, setDownloadOnly] = useState(typeof props?.downloadOnly === 'boolean' ? props.downloadOnly : false);

    const fileInputRef = useRef();
    const downloadRef = useRef();

    useEffect(() => {
        setFiles((prev) => (props?.files ? props.files : prev));
    }, [props.files]);

    useEffect(() => {
        setDisabled(doingAjax);
    }, [doingAjax]);

    useEffect(() => {
        if (retrieving) {
            const retrieveFile = async () => {
                try {
                    const _fileURL = await API.get('ShipyardAPI', `/application/docs/${files[0].fileId}`);

                    setDownloadURL(_fileURL.downloadURL);
                } catch (e) {
                    console.error(e);
                }
            };
            setRetrieving(false);
            retrieveFile();
        }
    }, [retrieving, files]);

    useEffect(() => {
        if (triggerValidation && required) {
            if (files.length === 0) {
                setValid(false);
            } else {
                setValid(true);
            }

            validationFinished(true);
        }
    }, [triggerValidation, validationFinished, files, required]);

    useEffect(() => {
        if (typeof downloadRef.current?.click === 'function' && !!downloadURL) {
            downloadRef.current.click();
            setDownloadURL();
        }
    }, [downloadRef, downloadURL]);

    useEffect(() => {
        if (typeof props.disabled === 'boolean') setDisabled(props.disabled);
    }, [props.disabled]);

    useEffect(() => {
        if (typeof props.required === 'boolean') setRequired(props.required);
    }, [props.required]);

    useEffect(() => {
        if (typeof props.downloadOnly === 'boolean') setDownloadOnly(props.downloadOnly);
    }, [props.downloadOnly]);

    useEffect(() => {
        if (typeof props.uploading === 'boolean') {
            setUploading(props.uploading);
            setDisabled(props.uploading);
        }
    }, [props.uploading]);

    const fileDownload = (event) => {
        if (event.target !== downloadRef.current) {
            setRetrieving(true);
        }
    };

    const fileDropJSX =
        files.length === 0 ? (
            <div className="file-drop-area">
                <div
                    className={classes}
                    onDrop={typeof props?.drop === 'function' && disabled !== true ? props.drop : null}
                    onDragOver={typeof props?.dragover === 'function' && disabled !== true ? props.dragover : null}
                >
                    <div className="file-icon">
                        {!uploading ? (
                            <UploadIcon className="upload-icon" />
                        ) : (
                            <FontAwesomeIcon
                                className="save-icon"
                                icon={faSpinner}
                            />
                        )}
                    </div>
                    <div>
                        {t('FileDrop.DropText')}
                        <br />
                        <span
                            className="blue-text"
                            onClick={
                                disabled !== true
                                    ? () => {
                                          fileInputRef.current.click();
                                      }
                                    : null
                            }
                            disabled={disabled === true}
                        >
                            {t('FileDrop.ClickText')}
                        </span>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={typeof props?.drop === 'function' ? props.drop : null}
                            data-key={!!props?.refKey ? props.refKey : null}
                        />
                    </div>
                </div>
                {!valid ? (
                    <div
                        className="invalid-reason"
                        id={'file_drop_err-' + uid}
                    >
                        This file is required.
                    </div>
                ) : null}
            </div>
        ) : (
            <div className={classes + ' dropped'}>
                {files.map((file) => {
                    return (
                        <div
                            className="files-list"
                            key={`file-${file.name}`}
                        >
                            <div>
                                <span>{file.name}</span>
                                <span>
                                    {t('FileDrop.UploadedOnLabel') + ' '}
                                    <DateTimeText
                                        className="file-drop-date-time"
                                        date={file.uploadedOn}
                                        type="short"
                                    />
                                </span>
                                {!!downloadURL && files[0]?.name ? (
                                    <a
                                        href={downloadURL}
                                        download={files[0].name}
                                        className="hidden"
                                        ref={downloadRef}
                                    >
                                        {files[0].name}
                                    </a>
                                ) : null}
                            </div>
                            <div className="type-icon">
                                &nbsp;
                                <DownloadIcon onClick={fileDownload} />
                            </div>
                            {!downloadOnly ? (
                                <div className="remove-icon">
                                    &nbsp;
                                    <DeleteIcon
                                        onClick={typeof props?.remove === 'function' ? () => props.remove(files[0].fileId) : null}
                                    />
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        );

    return (
        <div className="file-drop-container">
            {!!labelText ? (
                <label>
                    <>
                        {labelText}
                        <br />
                    </>
                </label>
            ) : null}
            {fileDropJSX}
            <input
                type="hidden"
                className="hidden"
                ref={ref}
                value={!!files[0]?.name ? files[0].name : ''}
                data-form-key={!!props.formKey ? props.formKey : `file-drop-${uid}`}
            />
        </div>
    );
});

export default FileDrop;
